import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'CM',
    reportsTo: '',
    propertyAssignments: [],
  });
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersData, propertiesData] = await Promise.all([
        api.get('/users'),
        api.get('/properties'),
      ]);
      setUsers(usersData);
      setProperties(propertiesData);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        name: user.name,
        password: '',
        role: user.role,
        reportsTo: user.reportsTo || '',
        propertyAssignments: user.propertyAssignments || [],
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        name: '',
        password: '',
        role: 'CM',
        reportsTo: '',
        propertyAssignments: [],
      });
    }
    setError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingUser) {
        const updateData = {
          name: formData.name,
          role: formData.role,
          reportsTo: formData.reportsTo || null,
          propertyAssignments: formData.propertyAssignments,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await api.put(`/users/${editingUser.id}`, updateData);
      } else {
        await api.post('/users', formData);
      }
      await loadData();
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await api.delete(`/users/${userId}`);
      await loadData();
    } catch (err) {
      alert('Failed to delete user: ' + err.message);
    }
  };

  const handlePropertyToggle = (propertyId) => {
    setFormData((prev) => {
      const assignments = prev.propertyAssignments.includes(propertyId)
        ? prev.propertyAssignments.filter((id) => id !== propertyId)
        : [...prev.propertyAssignments, propertyId];
      return { ...prev, propertyAssignments: assignments };
    });
  };

  const getUserName = (userId) => {
    const user = users.find((u) => u.id === userId);
    return user ? user.name : 'Unknown';
  };

  const getPropertyName = (propertyId) => {
    const property = properties.find((p) => p.id === propertyId);
    return property ? property.name : 'Unknown';
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Users Management</h1>
        <p className="page-subtitle">Manage users, roles, and reporting hierarchy</p>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">All Users</h2>
          <button className="btn btn-primary" onClick={() => handleOpenModal()} data-testid="create-user-btn">
            + Create User
          </button>
        </div>

        {users.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <h3 className="empty-state-title">No Users Yet</h3>
            <p className="empty-state-description">Get started by creating your first user.</p>
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>Create User</button>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Reports To</th>
                  <th>Properties</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td style={{ fontWeight: 500 }}>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className="badge badge-info">{user.role.replace('_', ' ')}</span>
                    </td>
                    <td>{user.reportsTo ? getUserName(user.reportsTo) : '-'}</td>
                    <td>
                      {user.propertyAssignments && user.propertyAssignments.length > 0
                        ? user.propertyAssignments.map((propId) => getPropertyName(propId)).join(', ')
                        : '-'}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="btn btn-secondary btn-small"
                          onClick={() => handleOpenModal(user)}
                          data-testid={`edit-user-${user.id}`}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-danger btn-small"
                          onClick={() => handleDelete(user.id)}
                          data-testid={`delete-user-${user.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingUser ? 'Edit User' : 'Create New User'}</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="form-error" style={{ marginBottom: '1rem' }}>{error}</div>}
                
                <div className="form-group">
                  <label className="form-label" htmlFor="name">Full Name</label>
                  <input
                    id="name"
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="user-name-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    type="email"
                    className="form-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    disabled={!!editingUser}
                    data-testid="user-email-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="password">
                    Password {editingUser && '(leave blank to keep current)'}
                  </label>
                  <input
                    id="password"
                    type="password"
                    className="form-input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                    data-testid="user-password-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="role">Role</label>
                  <select
                    id="role"
                    className="form-select"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    required
                    data-testid="user-role-select"
                  >
                    <option value="CM">Community Manager</option>
                    <option value="AREA_MANAGER">Area Manager</option>
                    <option value="REGIONAL">Regional Director</option>
                    <option value="VP">VP</option>
                    <option value="EXEC">Executive</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="reportsTo">Reports To</label>
                  <select
                    id="reportsTo"
                    className="form-select"
                    value={formData.reportsTo}
                    onChange={(e) => setFormData({ ...formData, reportsTo: e.target.value })}
                    data-testid="user-reportsTo-select"
                  >
                    <option value="">-- No Manager --</option>
                    {users
                      .filter((u) => u.id !== editingUser?.id)
                      .map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.role.replace('_', ' ')})
                        </option>
                      ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Property Assignments</label>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--gray-300)', borderRadius: '6px', padding: '0.75rem' }}>
                    {properties.length === 0 ? (
                      <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>No properties available</p>
                    ) : (
                      properties.map((property) => (
                        <label key={property.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.propertyAssignments.includes(property.id)}
                            onChange={() => handlePropertyToggle(property.id)}
                            style={{ marginRight: '0.5rem' }}
                            data-testid={`property-checkbox-${property.id}`}
                          />
                          <span style={{ fontSize: '0.875rem' }}>{property.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal} data-testid="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" data-testid="save-user-btn">
                  {editingUser ? 'Save Changes' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersManagement;
